import { Link, useLocation } from 'react-router-dom'
import {
  Heart,
  Users,
  Truck,
  Building2,
  BookOpen,
  ChevronDown,
  Info,
  MessageSquare,
  Shield,
  Sparkles,
} from 'lucide-react'
import { useUser } from '../contexts/UserContext'
import ProfileDropdown from './ProfileDropdown'
import { useState, useEffect, useRef } from 'react'

const ADMIN_PATHS = ['/admin/stories', '/admin/food-banks', '/admin/approvals', '/admin/feedback']

const Navbar = () => {
  const location = useLocation()
  const { isAuthenticated, user } = useUser()
  const [showLearnMore, setShowLearnMore] = useState(false)
  const [showAdmin, setShowAdmin] = useState(false)
  const learnMoreRef = useRef<HTMLDivElement>(null)
  const adminRef = useRef<HTMLDivElement>(null)

  const isActive = (path: string) => location.pathname === path
  const isAdmin = user?.userType === 'ADMIN'
  const isAdminRouteActive = ADMIN_PATHS.some((p) => location.pathname.startsWith(p))

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (learnMoreRef.current && !learnMoreRef.current.contains(event.target as Node)) {
        setShowLearnMore(false)
      }
      if (adminRef.current && !adminRef.current.contains(event.target as Node)) {
        setShowAdmin(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const navLinkClass = (path: string, extra = '') =>
    `nav-link whitespace-nowrap flex items-center shrink-0 ${isActive(path) ? 'nav-link-active' : ''} ${extra}`

  return (
    <nav className="bg-white/90 backdrop-blur-md shadow-sm border-b border-neutral-100 sticky top-0 z-50">
      <div className="max-w-[100rem] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4 min-h-[4.25rem] py-3">
          <Link to="/" className="flex items-center gap-3 group shrink-0">
            <img
              src="/harvesthelp-logo.png"
              alt="HarvestHelp"
              className="w-10 h-10 rounded-xl object-cover shadow-md group-hover:shadow-lg group-hover:scale-105 transition-all duration-200"
            />
            <span className="text-xl font-bold text-neutral-900 group-hover:text-primary-700 transition-colors duration-200 tracking-tight hidden sm:inline">
              HarvestHelp
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-2 xl:gap-3 flex-1 justify-center min-w-0">
            <Link to="/" className={navLinkClass('/')}>
              Home
            </Link>
            <Link to="/food-banks" className={navLinkClass('/food-banks')}>
              <Building2 className="h-4 w-4 mr-1.5 shrink-0" />
              Food Banks
            </Link>
            <Link to="/suppliers" className={navLinkClass('/suppliers')}>
              <Truck className="h-4 w-4 mr-1.5 shrink-0" />
              Suppliers
            </Link>
            {isAuthenticated && user?.userType !== 'VOLUNTEER' && (
              <Link to="/volunteers" className={navLinkClass('/volunteers')}>
                <Users className="h-4 w-4 mr-1.5 shrink-0" />
                Volunteers
              </Link>
            )}
            <Link to="/blog" className={navLinkClass('/blog')}>
              <BookOpen className="h-4 w-4 mr-1.5 shrink-0" />
              Stories
            </Link>

            <div className="relative shrink-0" ref={learnMoreRef}>
              <button
                type="button"
                onClick={() => {
                  setShowLearnMore(!showLearnMore)
                  setShowAdmin(false)
                }}
                className={`nav-link flex items-center whitespace-nowrap ${showLearnMore ? 'nav-link-active' : ''}`}
              >
                <Info className="h-4 w-4 mr-1.5 shrink-0" />
                Learn More
                <ChevronDown
                  className={`ml-1 h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${showLearnMore ? 'rotate-180' : ''}`}
                />
              </button>

              {showLearnMore && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-large border border-neutral-200 py-3 z-50 animate-slide-up">
                  <Link
                    to="/our-story"
                    className="flex items-center px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-primary-600 transition-colors duration-200"
                    onClick={() => setShowLearnMore(false)}
                  >
                    <Heart className="h-4 w-4 mr-3 shrink-0" />
                    Our Story
                  </Link>
                  <Link
                    to="/how-it-works"
                    className="flex items-center px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-primary-600 transition-colors duration-200"
                    onClick={() => setShowLearnMore(false)}
                  >
                    <Sparkles className="h-4 w-4 mr-3 shrink-0" />
                    How It Works
                  </Link>
                  <div className="border-t border-neutral-200 my-2" />
                  <Link
                    to="/blog"
                    className="flex items-center px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-primary-600 transition-colors duration-200"
                    onClick={() => setShowLearnMore(false)}
                  >
                    <BookOpen className="h-4 w-4 mr-3 shrink-0" />
                    Stories
                  </Link>
                  <div className="border-t border-neutral-200 my-2" />
                  <a
                    href="https://globalhungerdashboard.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-primary-600 transition-colors duration-200"
                    onClick={() => setShowLearnMore(false)}
                  >
                    <svg className="h-4 w-4 mr-3 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                    </svg>
                    Global Hunger Dashboard
                  </a>
                  <a
                    href="https://garrbearsblog.wordpress.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-primary-600 transition-colors duration-200"
                    onClick={() => setShowLearnMore(false)}
                  >
                    <svg className="h-4 w-4 mr-3 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 2c5.514 0 10 4.486 10 10s-4.486 10-10 10S2 17.514 2 12 6.486 2 12 2zm-1 3v8h8v-2h-6V5h-2z" />
                    </svg>
                    Garrett&apos;s Personal Blog
                  </a>
                </div>
              )}
            </div>

            {isAdmin && (
              <div className="relative shrink-0 ml-1 xl:ml-2 pl-2 xl:pl-3 border-l border-neutral-200" ref={adminRef}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAdmin(!showAdmin)
                    setShowLearnMore(false)
                  }}
                  className={`nav-link flex items-center whitespace-nowrap ${
                    showAdmin || isAdminRouteActive ? 'nav-link-active' : ''
                  }`}
                >
                  <Shield className="h-4 w-4 mr-1.5 shrink-0" />
                  Admin
                  <ChevronDown
                    className={`ml-1 h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${showAdmin ? 'rotate-180' : ''}`}
                  />
                </button>

                {showAdmin && (
                  <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-xl shadow-large border border-neutral-200 py-2 z-50 animate-slide-up">
                    <Link
                      to="/admin/approvals"
                      className="flex items-center px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-primary-600 transition-colors"
                      onClick={() => setShowAdmin(false)}
                    >
                      <Shield className="h-4 w-4 mr-3 shrink-0" />
                      Approvals
                    </Link>
                    <Link
                      to="/admin/stories"
                      className="flex items-center px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-primary-600 transition-colors"
                      onClick={() => setShowAdmin(false)}
                    >
                      <BookOpen className="h-4 w-4 mr-3 shrink-0" />
                      Stories
                    </Link>
                    <Link
                      to="/admin/feedback"
                      className="flex items-center px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-primary-600 transition-colors"
                      onClick={() => setShowAdmin(false)}
                    >
                      <MessageSquare className="h-4 w-4 mr-3 shrink-0" />
                      Feedback
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 shrink-0">
            {isAuthenticated ? (
              <ProfileDropdown />
            ) : (
              <>
                <Link to="/login" className="btn-ghost whitespace-nowrap">
                  Login
                </Link>
                <Link to="/register" className="btn-primary whitespace-nowrap">
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  )
}

export default Navbar
